import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { Icons } from "../../assest/icons";
import Header from './header';
import LoaderSpinner from '../loaderSpinner';
import { logger } from '../../utils/logger';
import { sendEmail } from '../../services/sendgridService';
import { isMA } from '../../utils/roleHelper';
import SideBarMenu from './sideBarMenu';
import {
  getPCPCasesWithFilters,
  getAllPCPUsers,
  getAllGIUsers,
  assignGIToPCPCase,
  getPCPCaseDetails,
  loadPCPDocumentBlobUrl,
  getGISharedReport,
} from '../../services/firestore';
import moment from 'moment';

// Safely parse date strings / Firestore Timestamps
const parseDate = (val) => {
  if (!val) return null;
  if (typeof val === 'object' && (val.seconds != null || val._seconds != null)) {
    return new Date((val.seconds || val._seconds) * 1000);
  }
  if (typeof val?.toDate === 'function') return val.toDate();
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

// Extract patient name from pcp_cases title field.
// PCP portal stores title as "Patient Name · health summary..."
const extractPatientName = (title) => {
  if (!title) return '—';
  const dotIndex = title.indexOf('·');
  if (dotIndex !== -1) return title.slice(0, dotIndex).trim();
  // Fallback: truncate to 40 chars
  return title.length > 40 ? title.slice(0, 40) + '…' : title;
};

const STATUS_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under Review',
  shared_with_pcp: 'Shared with PCP',
  completed: 'Completed',
  closed: 'Closed',
};

const STATUS_BADGE_CLASS = {
  draft: 'portal-status-draft',
  submitted: 'portal-status-pending',
  under_review: 'portal-status-assigned',
  shared_with_pcp: 'portal-status-active',
  completed: 'portal-status-active',
  closed: 'portal-status-inactive',
};

const DOC_KIND_LABELS = {
  lab: 'Lab Report',
  imaging: 'Imaging',
  note: 'Clinical Note',
  insurance_card_front: 'Insurance Card (Front)',
  insurance_card_back: 'Insurance Card (Back)',
  other: 'Other',
};

const URGENCY_COLORS = {
  routine:   { bg: '#D1FAE5', color: '#065F46' },
  urgent:    { bg: '#FEF3C7', color: '#92400E' },
  emergency: { bg: '#FEE2E2', color: '#991B1B' },
};

// Inline SVG icon for modal close button
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

function PCPCases() {
  // ─── Admin Info ───────────────────────────────────────────────
  const adminId = localStorage.getItem('UserIdAdmin') || '';
  const adminProfile = (() => {
    try { return JSON.parse(localStorage.getItem('UserProfileAdmin') || '{}'); }
    catch { return {}; }
  })();
  const adminEmail = adminProfile.email || '';

  // ─── Data ─────────────────────────────────────────────────────
  const [allCases, setAllCases] = useState([]);
  const [pcpUsers, setPcpUsers] = useState([]);
  const [giUsers, setGiUsers] = useState([]);
  const [showLoader, setShowLoader] = useState(true);

  // ─── Filters ──────────────────────────────────────────────────
  const [searchText, setSearchText] = useState('');
  const [checkString, setCheckString] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterGI, setFilterGI] = useState('all');
  const [filterPCP, setFilterPCP] = useState('all');

  // ─── Pagination ───────────────────────────────────────────────
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // ─── Case Detail Modal ────────────────────────────────────────
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailCase, setDetailCase] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // ─── Document Viewer ──────────────────────────────────────────
  const [loadingDocId, setLoadingDocId] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const docBlobCache = useRef({});

  // ─── Report Download ─────────────────────────────────────────
  const [downloadingReport, setDownloadingReport] = useState(false);

  // ─── GI Assignment Modal ──────────────────────────────────────
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [selectedGI, setSelectedGI] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [loadingGI, setLoadingGI] = useState(false);

  // ─── Fetch PCP users once on mount ───────────────────────────
  useEffect(() => {
    const loadPCPUsers = async () => {
      try {
        const users = await getAllPCPUsers();
        setPcpUsers(users);
      } catch (error) {
        logger.error('Error loading PCP users:', error);
      }
    };
    loadPCPUsers();
  }, []);

  // ─── Fetch cases whenever filters / pagination change ────────
  const fetchCases = useCallback(async (page = currentPage) => {
    setShowLoader(true);
    try {
      const result = await getPCPCasesWithFilters({
        page,
        perPage: recordsPerPage,
        searchText,
        filterStatus,
        filterGI,
        filterPCP,
        pcpUsersMap: pcpUsers,
        onlySharedWithMa: isMA(),
      });
      setAllCases(result.cases);
      setTotalPages(result.total_pages);
      setTotalRecords(result.total_records);
    } catch (error) {
      logger.error('Error fetching PCP cases:', error);
      toast.error('Failed to load cases', { position: toast.POSITION.TOP_RIGHT });
    }
    setShowLoader(false);
  }, [currentPage, recordsPerPage, searchText, filterStatus, filterGI, filterPCP, pcpUsers]);

  // Re-fetch when pcpUsers loads (so names resolve) or filters change
  useEffect(() => {
    fetchCases(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcpUsers, filterStatus, filterGI, filterPCP, recordsPerPage, currentPage]);

  // ─── Search handler (debounced via button / enter) ────────────
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCheckString(e.target.value.length > 0);
    setCurrentPage(1);
  };

  const handleResetSearch = () => {
    setSearchText('');
    setCheckString(false);
    setCurrentPage(1);
  };

  // Trigger search on Enter key
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setCurrentPage(1);
      fetchCases(1);
    }
  };

  // ─── Pagination ───────────────────────────────────────────────
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // ─── Open GI Assignment Modal ─────────────────────────────────
  const handleOpenAssignModal = async (e, caseItem) => {
    e.stopPropagation();
    setSelectedCase(caseItem);
    setSelectedGI(caseItem.sharedWithGiUserId || '');
    setShowAssignModal(true);

    if (giUsers.length === 0) {
      setLoadingGI(true);
      try {
        const gis = await getAllGIUsers();
        // Show only active GI users
        setGiUsers(gis.filter(g => g.accountStatus === 'active' || g.active === true));
      } catch (error) {
        logger.error('Error loading GI users:', error);
        toast.error('Failed to load GI specialists', { position: toast.POSITION.TOP_RIGHT });
      }
      setLoadingGI(false);
    }
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setSelectedCase(null);
    setSelectedGI('');
  };

  // ─── Case Detail Modal handlers ───────────────────────────────
  const handleOpenDetailModal = async (caseItem) => {
    setShowDetailModal(true);
    setDetailCase({ ...caseItem }); // show basic data immediately
    setLoadingDetail(true);
    try {
      const full = await getPCPCaseDetails(caseItem.id);
      setDetailCase({ ...caseItem, ...full });
    } catch (error) {
      logger.error('Error loading case details:', error);
      toast.error('Failed to load case details', { position: toast.POSITION.TOP_RIGHT });
    }
    setLoadingDetail(false);
  };

  const handleCloseDetailModal = () => {
    // Revoke cached blob URLs to free memory
    Object.values(docBlobCache.current).forEach(url => {
      try { URL.revokeObjectURL(url); } catch (e) {}
    });
    docBlobCache.current = {};
    setViewingDoc(null);
    setLoadingDocId(null);
    setShowDetailModal(false);
    setDetailCase(null);
  };

  // ─── Load + view a document ───────────────────────────────────
  const handleViewDocument = async (docItem) => {
    // Serve from cache if already loaded
    if (docBlobCache.current[docItem.id]) {
      setViewingDoc({ ...docItem, blobUrl: docBlobCache.current[docItem.id] });
      return;
    }
    if (!docItem.chunkCount || docItem.chunkCount === 0) {
      toast.error('Document data is not available', { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    setLoadingDocId(docItem.id);
    try {
      const blobUrl = await loadPCPDocumentBlobUrl(
        detailCase.id,
        docItem.id,
        docItem.chunkCount,
        docItem.contentType
      );
      docBlobCache.current[docItem.id] = blobUrl;
      setViewingDoc({ ...docItem, blobUrl });
    } catch (error) {
      logger.error('Error loading document:', error);
      toast.error('Failed to load document. Please try again.', { position: toast.POSITION.TOP_RIGHT });
    }
    setLoadingDocId(null);
  };

  // ─── Save GI Assignment ───────────────────────────────────────
  const handleSaveAssignment = async () => {
    if (!selectedCase) return;
    if (!selectedGI) {
      toast.error('Please select a GI Specialist', { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    const giUser = giUsers.find(g => g.id === selectedGI);
    if (!giUser) {
      toast.error('Selected GI Specialist not found', { position: toast.POSITION.TOP_RIGHT });
      return;
    }

    setAssigning(true);
    try {
      await assignGIToPCPCase(
        selectedCase.id,
        giUser,
        { adminId, adminEmail }
      );
      toast.success(
        `GI Specialist "${giUser.displayName || giUser.fullName}" assigned successfully!`,
        { position: toast.POSITION.TOP_RIGHT }
      );

      // Send email notification to GI specialist (fire-and-forget)
      const giEmail = giUser.email;
      if (giEmail) {
        const giName = giUser.displayName || giUser.fullName || 'GI Specialist';
        const patientName = extractPatientName(selectedCase.title) || 'Patient';
        const sharedDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        sendEmail({
          to: giEmail,
          subject: `New Case Assigned to You — ${patientName}`,
          html: `
            <div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;">
              <p style="font-size:15px;color:#333;">Hello Dr. <strong>${giName}</strong>,</p>
              <p style="font-size:15px;color:#333;">A new patient case has been assigned to you by the Medical Assistant team.</p>
              <table style="width:100%;border-collapse:collapse;margin:16px 0;">
                <tr><td style="padding:8px 12px;color:#666;width:140px;">Patient</td><td style="padding:8px 12px;font-weight:600;">${patientName}</td></tr>
                <tr style="background:#f8f9fa;"><td style="padding:8px 12px;color:#666;">Assigned By</td><td style="padding:8px 12px;">${adminEmail}</td></tr>
                <tr><td style="padding:8px 12px;color:#666;">Date</td><td style="padding:8px 12px;">${sharedDate}</td></tr>
              </table>
              <p style="font-size:15px;color:#333;">Please log in to the GI Portal to review and act on this case.</p>
              <p style="margin:24px 0;"><a href="https://gi.aigicare.com" style="display:inline-block;padding:12px 24px;background:#024C9C;color:#fff;text-decoration:none;border-radius:6px;font-weight:600;font-size:15px;">Open GI Portal</a></p>
              <p style="font-size:13px;color:#999;margin-top:32px;">— AIGI Care</p>
            </div>`,
          text: `Hello Dr. ${giName}, a new patient case for ${patientName} has been assigned to you. Please log in to the GI Portal to review: https://gi.aigicare.com`,
        }).catch((err) => logger.error('[MA] GI assignment email failed:', err));
      }

      handleCloseAssignModal();
      fetchCases(currentPage);
    } catch (error) {
      logger.error('Error assigning GI:', error);
      toast.error('Failed to assign GI Specialist. Please try again.', { position: toast.POSITION.TOP_RIGHT });
    }
    setAssigning(false);
  };

  // ─── Download GI Report PDF (fetches pre-generated PDF from Firebase Storage) ─
  const handleDownloadReport = async (caseId, patientName) => {
    setDownloadingReport(true);
    try {
      const report = await getGISharedReport(caseId);
      if (!report) {
        toast.error('GI report not available for this case yet.', { position: toast.POSITION.TOP_RIGHT });
        setDownloadingReport(false);
        return;
      }

      const pdfUrl = report.report_pdf_url;
      if (!pdfUrl) {
        toast.error('Report PDF is not available. Please ask the GI Specialist to re-share this case.', { position: toast.POSITION.TOP_RIGHT });
        setDownloadingReport(false);
        return;
      }

      // Fetch PDF blob and trigger download with proper filename
      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
      const blob = await response.blob();
      const safeName = (patientName || 'Patient').replace(/[^a-zA-Z0-9]/g, '_');
      const filename = `${moment().format('YYYY-MM-DD')}_${safeName}_GI_Report.pdf`;
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = filename;
      downloadLink.click();
      setTimeout(() => URL.revokeObjectURL(downloadLink.href), 10000);
      toast.success('Report downloaded successfully', { position: toast.POSITION.TOP_RIGHT });
    } catch (error) {
      logger.error('Error downloading GI report:', error);
      toast.error('Failed to download report. Please try again.', { position: toast.POSITION.TOP_RIGHT });
    }
    setDownloadingReport(false);
  };

  // ─── Table Body ───────────────────────────────────────────────
  const renderTableBody = () => {
    if (!allCases || allCases.length === 0) {
      return (
        <tr>
          <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
            No cases found
          </td>
        </tr>
      );
    }

    return allCases.map((caseItem, index) => {
      const status = caseItem.status || 'draft';
      const isAssigned = !!caseItem.sharedWithGiUserId;
      const isReportShared = status === 'shared_with_pcp' || status === 'completed';
      const submittedDate = parseDate(caseItem.submittedAt);
      const canAssign = status === 'submitted' || status === 'under_review';

      return (
        <tr
          key={caseItem.id || index}
          onClick={() => handleOpenDetailModal(caseItem)}
          style={{ cursor: 'pointer' }}
        >
          <td>
            <span style={{ fontFamily: 'monospace', fontSize: '13px', color: '#024C9C', fontWeight: '600' }}>
              {caseItem.shortCode || '—'}
            </span>
          </td>
          <td>
            <span style={{ fontWeight: '500' }}>
              {extractPatientName(caseItem.title)}
            </span>
          </td>
          <td style={{ color: '#555' }}>
            {caseItem.pcpUserName || '—'}
          </td>
          <td style={{ color: '#777', fontSize: '13px' }}>
            {submittedDate ? moment(submittedDate).format('MMM DD, YYYY') : '—'}
          </td>
          <td>
            <span
              className={`portal-status-badge ${STATUS_BADGE_CLASS[status] || ''}`}
              style={status === 'draft' ? { background: '#F3F4F6', color: '#6B7280', border: '1px solid #D1D5DB' } : {}}
            >
              {STATUS_LABELS[status] || status}
            </span>
          </td>
          <td>
            {isAssigned ? (
              <span style={{ fontSize: '13px', color: '#065F46', fontWeight: '500' }}>
                {caseItem.sharedWithGiUser || 'Assigned'}
              </span>
            ) : (
              <span style={{ fontSize: '13px', color: '#999' }}>Unassigned</span>
            )}
          </td>
          <td onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {canAssign && !isAssigned ? (
              <button
                className="assignGIBtn"
                onClick={(e) => handleOpenAssignModal(e, caseItem)}
                style={{
                  padding: '6px 16px',
                  background: '#024C9C',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Assign GI
              </button>
            ) : null}
            {isReportShared && (
              <button
                onClick={() => handleDownloadReport(caseItem.pcp_case_id || caseItem.id, extractPatientName(caseItem.title))}
                disabled={downloadingReport}
                title="Download GI Report"
                style={{
                  padding: '6px 14px',
                  background: downloadingReport ? '#F3F4F6' : '#065F46',
                  color: downloadingReport ? '#9CA3AF' : '#fff',
                  border: 'none',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: downloadingReport ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Report
              </button>
            )}
            {!canAssign && !isAssigned && (
              <span style={{ fontSize: '12px', color: '#bbb' }}>—</span>
            )}
            </div>
          </td>
        </tr>
      );
    });
  };

  // ─── Pagination ───────────────────────────────────────────────
  const renderPagination = () => {
    if (totalRecords === 0) return null;

    const startPage = (Math.floor((currentPage - 1) / 3) * 3) + 1;
    const endPage = Math.min(startPage + 2, totalPages);
    const pages = [];

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`page-button ${i === currentPage ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
          Previous
        </button>
        {pages}
        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
          Next
        </button>
      </div>
    );
  };

  // ─── Case Detail Modal ────────────────────────────────────────
  const renderDetailModal = () => {
    if (!showDetailModal) return null;

    const c = detailCase || {};
    const about = c.about || {};
    const health = c.health || {};
    const pcp = c.pcpPharmacy || {};
    const documents = c.documents || [];
    const status = c.status || 'draft';
    const submittedDate = parseDate(c.submittedAt);

    const sectionStyle = { borderBottom: '1px solid #F0F0F0', padding: '20px 28px' };
    const secTitleStyle = { fontSize: '11px', fontWeight: '800', color: '#024C9C', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' };
    const labelStyle = { fontSize: '11px', fontWeight: '600', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' };
    const valueStyle = { fontSize: '14px', color: '#111827', fontWeight: '400', lineHeight: '1.5' };
    const gridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' };

    const Field = ({ label, value, full = false }) => {
      if (!value && value !== 0) return null;
      return (
        <div style={full ? { gridColumn: '1 / -1' } : {}}>
          <div style={labelStyle}>{label}</div>
          <div style={valueStyle}>{value}</div>
        </div>
      );
    };

    return (
      <div
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}
        onClick={handleCloseDetailModal}
      >
        <div
          style={{ background: '#fff', borderRadius: '16px', width: '740px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}
          onClick={e => e.stopPropagation()}
        >
          {/* ── Sticky Header ── */}
          <div style={{ position: 'sticky', top: 0, zIndex: 10, background: '#fff', borderRadius: '16px 16px 0 0', borderBottom: '2px solid #F0F4FF', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'monospace', fontSize: '14px', color: '#024C9C', fontWeight: '800', background: '#EFF6FF', padding: '5px 12px', borderRadius: '8px', border: '1px solid #BFDBFE' }}>
                {c.shortCode || c.id || '—'}
              </span>
              <span
                className={`portal-status-badge ${STATUS_BADGE_CLASS[status] || ''}`}
                style={status === 'draft' ? { background: '#F3F4F6', color: '#6B7280', border: '1px solid #D1D5DB' } : {}}
              >
                {STATUS_LABELS[status] || status}
              </span>
              {c.pcpUserName && (
                <span style={{ fontSize: '12px', color: '#6B7280', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '20px', padding: '3px 10px' }}>
                  PCP: {c.pcpUserName}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {(status === 'shared_with_pcp' || status === 'completed') && (
                <button
                  onClick={() => handleDownloadReport(c.pcp_case_id || c.id, extractPatientName(c.title))}
                  disabled={downloadingReport}
                  style={{
                    padding: '6px 14px',
                    background: downloadingReport ? '#F3F4F6' : '#024C9C',
                    color: downloadingReport ? '#9CA3AF' : '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: downloadingReport ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {downloadingReport ? (
                    <div style={{ width: '12px', height: '12px', border: '2px solid #E5E7EB', borderTopColor: '#024C9C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  ) : (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  )}
                  {downloadingReport ? 'Downloading...' : 'Download Report'}
                </button>
              )}
              <button onClick={handleCloseDetailModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: '4px', display: 'flex', borderRadius: '6px' }}>
                <CloseIcon />
              </button>
            </div>
          </div>

          {loadingDetail ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{ width: '32px', height: '32px', border: '3px solid #E5E7EB', borderTopColor: '#024C9C', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
              <div style={{ color: '#9CA3AF', fontSize: '14px' }}>Loading case details...</div>
            </div>
          ) : (
            <>
              {/* ── AI Summary ── */}
              {c.aiSummary && (
                <div style={{ ...sectionStyle, background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)' }}>
                  <div style={secTitleStyle}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#024C9C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
                    </svg>
                    AI Summary
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #BFDBFE', borderRadius: '10px', padding: '16px 18px', fontSize: '14px', color: '#1F2937', lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>
                    {c.aiSummary}
                  </div>
                </div>
              )}

              {/* ── Patient Information ── */}
              <div style={sectionStyle}>
                <div style={secTitleStyle}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#024C9C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  Patient Information
                </div>
                <div style={gridStyle}>
                  <Field label="Full Name" value={about.fullLegalName || extractPatientName(c.title)} />
                  <Field label="Date of Birth" value={about.dateOfBirth ? moment(about.dateOfBirth).format('MMM DD, YYYY') : null} />
                  <Field label="Gender" value={about.gender ? about.gender.charAt(0).toUpperCase() + about.gender.slice(1).replace(/_/g, ' ') : null} />
                  <Field label="State" value={about.state} />
                  <Field label="Phone" value={about.mobile} />
                  <Field label="Email" value={about.email} />
                  <Field label="Address" value={about.address} full />
                </div>
              </div>

              {/* ── Insurance ── */}
              {(() => {
                const carrier = about.insuranceCarrier || about.insurance_carrier || about.insuranceName || about.insurance_name;
                const policyId = about.policyId || about.policy_id || about.insurancePolicyId || about.insurance_policy_id;
                const groupName = about.groupName || about.group_name || about.insuranceGroup || about.insurance_group;
                const effectiveDate = about.effectiveDate || about.effective_date;
                const insuranceCards = documents.filter(d => d.kind === 'insurance_card_front' || d.kind === 'insurance_card_back');
                if (!carrier && !policyId && !groupName && !effectiveDate && insuranceCards.length === 0) return null;
                return (
                  <div style={sectionStyle}>
                    <div style={secTitleStyle}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#024C9C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                      </svg>
                      Insurance
                    </div>
                    <div style={gridStyle}>
                      <Field label="Insurance Carrier" value={carrier} />
                      <Field label="Policy ID" value={policyId} />
                      <Field label="Group Name" value={groupName} />
                      <Field label="Effective Date" value={effectiveDate} />
                    </div>
                    {/* Insurance card images */}
                    {insuranceCards.length > 0 && (
                      <div style={{ display: 'flex', gap: '10px', marginTop: '14px', flexWrap: 'wrap' }}>
                        {insuranceCards.map((card) => {
                          const isLoadingThis = loadingDocId === card.id;
                          return (
                            <button
                              key={card.id}
                              onClick={() => handleViewDocument(card)}
                              disabled={isLoadingThis}
                              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', cursor: isLoadingThis ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', color: '#024C9C' }}
                            >
                              {isLoadingThis ? (
                                <div style={{ width: '12px', height: '12px', border: '2px solid #BFDBFE', borderTopColor: '#024C9C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                              ) : (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                                </svg>
                              )}
                              {DOC_KIND_LABELS[card.kind] || card.kind}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── Health Details ── */}
              <div style={sectionStyle}>
                <div style={secTitleStyle}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#024C9C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                  </svg>
                  Health Details
                </div>

                {/* Urgency + Chief Complaint row */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
                  {health.urgencyLevel && (
                    <div>
                      <div style={labelStyle}>Urgency Level</div>
                      <span style={{ display: 'inline-block', padding: '4px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '700', marginTop: '4px', background: (URGENCY_COLORS[health.urgencyLevel] || {}).bg, color: (URGENCY_COLORS[health.urgencyLevel] || {}).color }}>
                        {health.urgencyLevel.charAt(0).toUpperCase() + health.urgencyLevel.slice(1)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Chief Complaint */}
                {health.inboxMessage && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={labelStyle}>Chief Complaint / Symptoms</div>
                    <div style={{ marginTop: '6px', background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: '10px', padding: '14px 16px', fontSize: '14px', color: '#1F2937', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
                      {health.inboxMessage}
                    </div>
                  </div>
                )}

                {/* BMI + Allergies row */}
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '16px 24px', marginBottom: '16px' }}>
                  {health.bmi && (
                    <div>
                      <div style={labelStyle}>BMI</div>
                      <div style={{ ...valueStyle, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '6px 14px', display: 'inline-block', fontWeight: '600' }}>{health.bmi}</div>
                    </div>
                  )}
                  {health.allergies && (
                    <div>
                      <div style={labelStyle}>Allergies</div>
                      <div style={{ ...valueStyle, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '6px 14px', lineHeight: '1.6' }}>{health.allergies}</div>
                    </div>
                  )}
                </div>

                <div style={gridStyle}>
                  <Field label="Current Medications" value={health.currentMedications} full />
                  <Field label="Existing Conditions" value={health.existingConditions} full />
                  <Field label="Past Surgical History" value={health.pastSurgicalHistory} full />
                  <Field label="Family History" value={health.familyHistory} full />
                  <Field label="Social History" value={health.socialHistory} full />
                  <Field label="Recent Tests / Procedures" value={health.recentTestsOrProcedures} full />
                  <Field label="Lifestyle Notes" value={health.lifestyleNotes} full />
                </div>

                {/* PCP & Pharmacy contact info — from pcp_pharmacy collection OR health doc fields */}
                {(() => {
                  const pcpName = pcp.pcp_name || health.primaryCarePhysician;
                  const pcpPhone = pcp.pcp_phone || health.pcpPhone || health.pcpPhoneFax;
                  const pcpFax = pcp.pcp_fax || health.pcpFax;
                  const pcpEmail = pcp.pcp_email;
                  const pcpAddress = pcp.pcp_address;
                  if (!pcpName && !pcpPhone && !pcpFax && !pcpEmail && !pcpAddress) return null;
                  return (
                    <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #F0F4FF' }}>
                      <div style={{ ...labelStyle, marginBottom: '10px', color: '#024C9C' }}>Primary Care Physician</div>
                      <div style={gridStyle}>
                        <Field label="PCP Name" value={pcpName} full />
                        <Field label="PCP Phone" value={pcpPhone} />
                        <Field label="PCP Fax" value={pcpFax} />
                        <Field label="PCP Email" value={pcpEmail} />
                        <Field label="PCP Address" value={pcpAddress} full />
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  const pharmName = pcp.pharmacy_name || health.pharmacyInformation;
                  const pharmPhone = pcp.pharmacy_phone || health.pharmacyPhone || health.pharmacyPhoneFax;
                  const pharmFax = pcp.pharmacy_fax || health.pharmacyFax;
                  const pharmEmail = pcp.pharmacy_email;
                  const pharmAddress = pcp.pharmacy_address;
                  if (!pharmName && !pharmPhone && !pharmFax && !pharmEmail && !pharmAddress) return null;
                  return (
                    <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #F0F4FF' }}>
                      <div style={{ ...labelStyle, marginBottom: '10px', color: '#024C9C' }}>Pharmacy</div>
                      <div style={gridStyle}>
                        <Field label="Pharmacy Name" value={pharmName} full />
                        <Field label="Pharmacy Phone" value={pharmPhone} />
                        <Field label="Pharmacy Fax" value={pharmFax} />
                        <Field label="Pharmacy Email" value={pharmEmail} />
                        <Field label="Pharmacy Address" value={pharmAddress} full />
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* ── Documents ── */}
              <div style={sectionStyle}>
                <div style={secTitleStyle}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#024C9C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                  </svg>
                  Documents
                  <span style={{ background: '#E0E7FF', color: '#3730A3', borderRadius: '20px', padding: '1px 8px', fontSize: '11px', fontWeight: '700' }}>
                    {documents.length}
                  </span>
                </div>
                {documents.length === 0 ? (
                  <div style={{ color: '#9CA3AF', fontSize: '13px' }}>No documents uploaded</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {documents.map((docItem, i) => {
                      const isImage = docItem.contentType && docItem.contentType.startsWith('image/');
                      const isPDF = docItem.contentType === 'application/pdf';
                      const sizeKB = docItem.sizeBytes ? Math.round(docItem.sizeBytes / 1024) : null;
                      const isLoadingThis = loadingDocId === docItem.id;
                      const hasChunks = docItem.chunkCount > 0;
                      return (
                        <div key={docItem.id || i} style={{ borderRadius: '10px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#F9FAFB' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                              {/* File icon */}
                              <div style={{ width: '34px', height: '34px', borderRadius: '7px', background: isPDF ? '#FEE2E2' : isImage ? '#DBEAFE' : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={isPDF ? '#DC2626' : isImage ? '#2563EB' : '#6B7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  {isImage
                                    ? <><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></>
                                    : <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></>
                                  }
                                </svg>
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {docItem.fileName || 'Untitled'}
                                </div>
                                <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>
                                  {DOC_KIND_LABELS[docItem.kind] || docItem.kind || 'Document'}
                                  {sizeKB ? ` · ${sizeKB} KB` : ''}
                                  {docItem.uploadedAt ? ` · ${moment(docItem.uploadedAt).format('MMM DD, YYYY')}` : ''}
                                </div>
                              </div>
                            </div>
                            {/* View button */}
                            {hasChunks && (
                              <button
                                onClick={() => handleViewDocument(docItem)}
                                disabled={isLoadingThis}
                                style={{ padding: '6px 16px', background: isLoadingThis ? '#F3F4F6' : '#EFF6FF', color: isLoadingThis ? '#9CA3AF' : '#024C9C', border: '1px solid #BFDBFE', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: isLoadingThis ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0, marginLeft: '10px' }}
                              >
                                {isLoadingThis ? (
                                  <div style={{ width: '12px', height: '12px', border: '2px solid #E5E7EB', borderTopColor: '#024C9C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                ) : (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                                  </svg>
                                )}
                                {isLoadingThis ? 'Loading...' : 'View'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── Case Meta + Action ── */}
              <div style={{ ...sectionStyle, borderBottom: 'none' }}>
                <div style={secTitleStyle}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#024C9C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  Case Details
                </div>
                <div style={gridStyle}>
                  <Field label="Submitted On" value={submittedDate ? moment(submittedDate).format('MMM DD, YYYY hh:mm A') : null} />
                  <Field label="Assigned GI Specialist" value={c.sharedWithGiUser || (c.sharedWithGiUserId ? 'Assigned' : null)} />
                  {c.sharedWithGiAt && <Field label="GI Assigned On" value={moment(parseDate(c.sharedWithGiAt)).format('MMM DD, YYYY')} />}
                </div>

                {(status === 'submitted' || status === 'under_review') && !c.sharedWithGiUserId && (
                  <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #F0F0F0' }}>
                    <button
                      onClick={(e) => { handleCloseDetailModal(); handleOpenAssignModal(e, c); }}
                      style={{ padding: '10px 28px', background: '#024C9C', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', letterSpacing: '0.2px' }}
                    >
                      Assign GI Specialist
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ─── Document Viewer Overlay ──────────────────────────────────
  const renderDocumentViewer = () => {
    if (!viewingDoc) return null;
    const isImage = viewingDoc.contentType && viewingDoc.contentType.startsWith('image/');

    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.92)', zIndex: 10000, display: 'flex', flexDirection: 'column' }}>
        {/* Top bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', background: '#111827', borderBottom: '1px solid #1F2937', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#F9FAFB', fontWeight: '600', fontSize: '14px' }}>{viewingDoc.fileName || 'Document'}</span>
            <span style={{ fontSize: '11px', color: '#9CA3AF', background: '#1F2937', padding: '2px 8px', borderRadius: '4px', border: '1px solid #374151' }}>
              {DOC_KIND_LABELS[viewingDoc.kind] || viewingDoc.kind || 'Document'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={() => window.open(viewingDoc.blobUrl, '_blank')}
              style={{ padding: '7px 16px', background: '#1F2937', color: '#E5E7EB', border: '1px solid #374151', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
            >
              Open in New Tab
            </button>
            <button
              onClick={() => setViewingDoc(null)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', background: '#1F2937', color: '#E5E7EB', border: '1px solid #374151', borderRadius: '6px', cursor: 'pointer' }}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '20px' }}>
          {isImage ? (
            <img
              src={viewingDoc.blobUrl}
              alt={viewingDoc.fileName}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 4px 24px rgba(0,0,0,0.5)' }}
            />
          ) : (
            <iframe
              src={viewingDoc.blobUrl}
              title={viewingDoc.fileName}
              style={{ width: '100%', height: '100%', border: 'none', borderRadius: '8px', background: '#fff' }}
            />
          )}
        </div>
      </div>
    );
  };

  // ─── GI Assignment Modal ──────────────────────────────────────
  const renderAssignModal = () => {
    if (!showAssignModal || !selectedCase) return null;

    const overlayStyle = {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.45)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    };
    const modalStyle = {
      background: '#fff', borderRadius: '16px', padding: '32px',
      width: '500px', maxWidth: '95vw',
      boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
    };

    return (
      <div style={overlayStyle} onClick={handleCloseAssignModal}>
        <div style={modalStyle} onClick={e => e.stopPropagation()}>
          {/* Modal Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111' }}>
              Assign GI Specialist
            </h3>
            <button
              onClick={handleCloseAssignModal}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: '4px', display: 'flex', alignItems: 'center' }}
            >
              <CloseIcon />
            </button>
          </div>

          {/* Case Info */}
          <div style={{ background: '#F8F9FC', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '13px', color: '#555' }}>
                <b style={{ color: '#333' }}>Case:</b>{' '}
                <span style={{ fontFamily: 'monospace', color: '#024C9C', fontWeight: '600' }}>
                  {selectedCase.shortCode || selectedCase.id}
                </span>
              </span>
              <span style={{ fontSize: '13px', color: '#555' }}>
                <b style={{ color: '#333' }}>Patient:</b> {extractPatientName(selectedCase.title)}
              </span>
              <span style={{ fontSize: '13px', color: '#555' }}>
                <b style={{ color: '#333' }}>PCP:</b> {selectedCase.pcpUserName || '—'}
              </span>
            </div>
          </div>

          {/* Current Assignment Info */}
          {selectedCase.sharedWithGiUser && (
            <div style={{ fontSize: '13px', color: '#065F46', background: '#D1FAE5', padding: '8px 14px', borderRadius: '8px', marginBottom: '16px' }}>
              Currently assigned to: <b>{selectedCase.sharedWithGiUser}</b>
            </div>
          )}

          {/* GI Dropdown */}
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '8px' }}>
            Select GI Specialist
          </label>
          {loadingGI ? (
            <div style={{ padding: '12px', textAlign: 'center', color: '#888', fontSize: '13px' }}>
              Loading GI specialists...
            </div>
          ) : (
            <select
              value={selectedGI}
              onChange={(e) => setSelectedGI(e.target.value)}
              style={{
                width: '100%', padding: '11px 14px',
                border: '1px solid #D1D5DB', borderRadius: '8px',
                fontSize: '14px', color: '#333', marginBottom: '24px',
                background: '#fff', cursor: 'pointer',
                outline: 'none',
              }}
            >
              <option value="">— Select a GI Specialist —</option>
              {giUsers.map(gi => (
                <option key={gi.id} value={gi.id}>
                  {gi.displayName || gi.fullName || gi.email}
                  {gi.specialty ? ` (${gi.specialty})` : ''}
                </option>
              ))}
            </select>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              onClick={handleCloseAssignModal}
              disabled={assigning}
              style={{
                padding: '10px 24px', background: '#fff',
                color: '#555', border: '1px solid #ddd',
                borderRadius: '8px', fontSize: '14px',
                fontWeight: '600', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAssignment}
              disabled={assigning || loadingGI || !selectedGI}
              style={{
                padding: '10px 28px',
                background: assigning || !selectedGI ? '#93B8E0' : '#024C9C',
                color: '#fff', border: 'none',
                borderRadius: '8px', fontSize: '14px',
                fontWeight: '600', cursor: assigning || !selectedGI ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
              }}
            >
              {assigning ? 'Assigning...' : 'Assign'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Render ───────────────────────────────────────────────────
  return (
    <div>
      {showLoader && <LoaderSpinner />}
      {renderDocumentViewer()}
      {renderDetailModal()}
      {renderAssignModal()}

      <div className='container-fluid p-0 adminDashboardMain'>
        <SideBarMenu />

        <div className='container-fluid p-0 contentBody'>
          <Header />
          <div className="innerBodyBox bgInnerDiv patientDataListMain">
            <div className="contentInnerBody">

              {/* ── Row 1: Title + Search ── */}
              <div className="titleDiv">
                <h2>PCP Cases</h2>
                <div className="leftDiv d-inline-block searchAdminUser">
                  <div className="inputDivBox">
                    <img src={Icons[0].admin_searchIcon} alt="search-icon" />
                    <input
                      type="text"
                      placeholder="Search case ID, patient name, PCP..."
                      value={searchText}
                      onChange={handleSearchChange}
                      onKeyDown={handleSearchKeyDown}
                    />
                    <img
                      className={"reset-button " + (checkString ? 'b' : 'd-none')}
                      src={Icons[0].admin_deleteIcon}
                      alt="clear-icon"
                      onClick={handleResetSearch}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </div>
              </div>

              {/* ── Row 2: Filters Bar ── */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '0 20px 16px' }}>
                {/* Status Filter */}
                <div style={{ border: '1px solid #E6E6E6', background: '#fff', borderRadius: '50px', padding: '8px 14px', fontSize: '13px' }}>
                  <div className="table-options">
                    <label htmlFor="filterStatusPCP">Status:</label>
                    <select
                      id="filterStatusPCP"
                      value={filterStatus}
                      onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                      style={{ border: 0, background: 'transparent', cursor: 'pointer', fontSize: '13px' }}
                    >
                      <option value="all">All</option>
                      <option value="draft">Draft</option>
                      <option value="submitted">Submitted</option>
                      <option value="under_review">Under Review</option>
                      <option value="completed">Completed</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>

                {/* GI Assignment Filter */}
                <div style={{ border: '1px solid #E6E6E6', background: '#fff', borderRadius: '50px', padding: '8px 14px', fontSize: '13px' }}>
                  <div className="table-options">
                    <label htmlFor="filterGIAssign">GI:</label>
                    <select
                      id="filterGIAssign"
                      value={filterGI}
                      onChange={(e) => { setFilterGI(e.target.value); setCurrentPage(1); }}
                      style={{ border: 0, background: 'transparent', cursor: 'pointer', fontSize: '13px' }}
                    >
                      <option value="all">All</option>
                      <option value="unassigned">Unassigned</option>
                      <option value="assigned">Assigned</option>
                    </select>
                  </div>
                </div>

                {/* PCP Filter */}
                <div style={{ border: '1px solid #E6E6E6', background: '#fff', borderRadius: '50px', padding: '8px 14px', fontSize: '13px' }}>
                  <div className="table-options">
                    <label htmlFor="filterPCPUser">PCP:</label>
                    <select
                      id="filterPCPUser"
                      value={filterPCP}
                      onChange={(e) => { setFilterPCP(e.target.value); setCurrentPage(1); }}
                      style={{ border: 0, background: 'transparent', cursor: 'pointer', fontSize: '13px', maxWidth: '160px' }}
                    >
                      <option value="all">All PCPs</option>
                      {pcpUsers.map(pcp => (
                        <option key={pcp.id} value={pcp.id}>
                          {pcp.name || pcp.fullName || pcp.email || pcp.id}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Total Count */}
                <div style={{ border: '1px solid #E6E6E6', background: '#fff', borderRadius: '50px', padding: '8px 14px', fontSize: '13px' }}>
                  Total: {totalRecords}
                </div>

                {/* Records Per Page */}
                <div style={{ border: '1px solid #E6E6E6', background: '#fff', borderRadius: '50px', padding: '8px 14px', fontSize: '13px' }}>
                  <div className="table-options">
                    <label htmlFor="pcpCasesPerPage">Showing</label>
                    <select
                      id="pcpCasesPerPage"
                      value={recordsPerPage}
                      onChange={(e) => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                      style={{ border: 0, background: 'transparent', cursor: 'pointer', fontSize: '13px' }}
                    >
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* ── Table ── */}
              <div className="contentMainBox">
                <div className="table-container">
                  <table id="pcpCasesTable">
                    <thead>
                      <tr>
                        <th>Case ID</th>
                        <th>Patient Name</th>
                        <th>PCP</th>
                        <th>Submitted On</th>
                        <th>Status</th>
                        <th>Assigned GI</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderTableBody()}
                    </tbody>
                  </table>
                </div>

                {/* ── Pagination ── */}
                <div className="paginationMain">
                  {renderPagination()}
                  <span className={"totalPageDiv " + (!totalPages ? 'd-none' : '')}>
                    Total Pages: {totalPages}
                  </span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PCPCases;
